import { marked } from 'marked';
import DOMPurify from 'dompurify';

import { getMarkedDisplayOptions } from './marked-display-options';

/**
 * Parses markdown to HTML with safe defaults for displaying user content
 * - Sanitizes HTML to prevent XSS attacks
 * - Opens links in new tabs
 * - Adds rel="noopener noreferrer" for security
 * - Supports custom underline syntax: __text__
 *
 * @param markdown - The markdown string to parse
 * @returns Sanitized HTML string, or empty string if parsing fails
 */
export function parseMarkdown(markdown: string): string {
  // Handle null/undefined/empty input
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  try {
    // First, convert custom underline syntax (__text__) to HTML <u> tags
    // We do this before marked.parse() to avoid conflicts with bold (**text**)
    const processed = markdown.replace(/__([\s\S]+?)__/g, '<u>$1</u>');

    // Configure marked renderer to open links in new tabs
    const renderer = new marked.Renderer();
    const originalLinkRenderer = renderer.link.bind(renderer);

    renderer.link = (href, title, text) => {
      const html = originalLinkRenderer(href, title, text);
      return html.replace(
        '<a ',
        '<a target="_blank" rel="noopener noreferrer" ',
      );
    };

    const html = marked.parse(
      processed,
      getMarkedDisplayOptions({ renderer }),
    ) as string;

    // Sanitize HTML with allowlist of safe tags and attributes
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'u',
        'a',
        'ul',
        'ol',
        'li',
        'blockquote',
        'h1',
        'h2',
        'hr',
        'code',
        'pre',
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
    });
  } catch (error) {
    // Log error for debugging but don't expose to user
    console.error('Failed to parse markdown:', error);
    // Return escaped plain text as fallback
    return markdown
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
